import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, useLocation } from "react-router-dom";
import {
  Container,
  Heading,
  Column,
  Row,
  url,
  BadRequestError,
  UnauthorizedError,
  useNotifier,
  useBrowser,
  useFlipperQuery,
  useTracking,
  useConfiguration,
  withBackend,
  backendShape,
  Button,
} from "lcm-iot-commons";

import SignInForm from "./SignInForm";
import SignInOtpForm from "./SignInOtpForm";
import MultiFactorAuthenticationForm from "../UserProfile/MultiFactorAuthenticationForm";
import RecoveryCodesModal from "../UserProfile/RecoveryCodesModal";
import EHSignInForm from "./EHSignInForm";

type Props = {
  backend: backendShape;
};

const extractApiErrorMessage = (error: $TSFixMe) =>
  error?.errors?.find((apiError: $TSFixMe) => apiError?.message)?.message;

export function SignIn({ backend }: Readonly<Props>) {
  const browser = useBrowser();
  const configuration = useConfiguration();
  const { data: flipper } = useFlipperQuery();

  const intl = useIntl();
  const location = useLocation();
  const notifier = useNotifier();
  const tracking = useTracking();

  const [captchaRequired, setCaptchaRequired] = useState(
    localStorage.getItem("captchaRequired") === "true",
  );
  const [otpState, setOtpState] = useState();
  const [otpEnforcedState, setOtpEnforcedState] = useState<{
    credentials: { email: string; password: string };
    provisioningUri?: string;
  }>();
  const [otpEnforcedFinishedState, setOtpEnforcedFinishedState] = useState<{
    recoveryCodes?: string[];
    redirectUri: string;
  }>();
  const [showNetilionLoginForm, setShowNetilionLoginForm] = useState(false);

  const captchaRefresh = useRef();
  const { googleCaptchaSiteKey } = configuration;

  const urlParams = useMemo(
    () => (location?.search ? new URLSearchParams(location.search) : null),
    [location?.search],
  );
  const getUrlParameter = (name: string) => urlParams?.get(name) ?? null;

  useEffect(() => {
    if (flipper && !flipper.loginWithMyEndressHauser) {
      setShowNetilionLoginForm(true);
    }

    localStorage.setItem(
      "captchaRequired",
      JSON.stringify(!!googleCaptchaSiteKey && captchaRequired),
    );
  }, [captchaRequired, flipper, googleCaptchaSiteKey]);

  const signIn = async (values: $TSFixMe) =>
    backend.post(
      "/sso_session",
      {
        email: values.email,
        password: values.password,
        captcha: values.captcha,
        otp_code_token: values.otpCodeToken,
        redirect_uri: getUrlParameter("redirect_uri"),
        client_id: getUrlParameter("client_id"),
        state: getUrlParameter("state"),
        code_challenge_method: getUrlParameter("code_challenge_method"),
        code_challenge: getUrlParameter("code_challenge"),
        scope: getUrlParameter("scope"),
        nonce: getUrlParameter("nonce"),
      },
      null,
      false,
    );

  const handleBadRequestError = (
    error: $TSFixMe,
    values: $TSFixMe,
    actions: $TSFixMe,
  ) => {
    if (error.contains("missing_login_token")) {
      setOtpState(values);
      return true;
    }

    if (error.contains("invalid_login_token")) {
      notifier.showError(intl.formatMessage({ id: "signin.otp.invalid" }));
      actions.resetForm({
        values: { email: values.email, password: values.password },
      });
      /* istanbul ignore next */
      if (otpState) {
        setOtpState(values);
      } else {
        setOtpEnforcedState({
          ...otpEnforcedState,
          credentials: { email: values.email, password: values.password },
        });
      }
      return true;
    }

    /* istanbul ignore else */
    if (error.contains("required_login_token")) {
      const provisioningUri = error.errors[0].data.provisioning_uri;
      setOtpEnforcedState({ credentials: values, provisioningUri });
      return true;
    }

    /* istanbul ignore next */
    return false;
  };

  const handleUnauthorizedError = (error: $TSFixMe) => {
    /* istanbul ignore else */
    if (error.contains("too_many_login_attempts")) {
      setCaptchaRequired(!!googleCaptchaSiteKey);
      if (otpState) setOtpState(undefined);
    }
    const invalidMessageId = flipper?.loginWithMyEndressHauser
      ? "signin.invalid.with_sso_hint"
      : "signin.invalid";
    notifier.showError(
      extractApiErrorMessage(error) ??
        intl.formatMessage({ id: invalidMessageId }),
    );
  };

  const handleSignInError = (
    error: $TSFixMe,
    values: $TSFixMe,
    actions: $TSFixMe,
  ) => {
    if (
      error instanceof BadRequestError &&
      handleBadRequestError(error, values, actions)
    ) {
      return;
    }

    if (error instanceof UnauthorizedError) {
      handleUnauthorizedError(error);
    } else {
      notifier.showError(
        extractApiErrorMessage(error) ??
          intl.formatMessage({ id: "api.error.service_unavailable" }),
      );
    }

    actions.setErrors(error);
    actions.resetForm({ values: {} });
    (captchaRefresh.current as $TSFixMe)?.reset();
  };

  const handleOnSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    try {
      const response = await signIn(values);

      localStorage.setItem("captchaRequired", JSON.stringify(false));

      if (otpEnforcedState) {
        setOtpEnforcedFinishedState({
          recoveryCodes: response.recovery_codes,
          redirectUri: response.redirect_uri,
        });
      } else {
        tracking.trackEvent("SignIn-Successful");
        browser.redirectToExtern(response.redirect_uri);
      }
    } catch (error) {
      handleSignInError(error, values, actions);
    }
  };

  const handleOnCloseRecoveryCodesModal = () => {
    /* istanbul ignore next */
    if (!otpEnforcedFinishedState) return;
    const { redirectUri } = otpEnforcedFinishedState;
    setOtpEnforcedFinishedState(undefined);
    browser.redirectToExtern(redirectUri);
  };

  const renderLoginChoice = () => {
    if (otpState || otpEnforcedState || !flipper) return null;

    return (
      <Row className="signin-content">
        {flipper.loginWithMyEndressHauser && (
          <Column md="5" className="signin-eh-login-form">
            <div>
              <div className="signin-eh-logo" />
              <EHSignInForm
                clientId={getUrlParameter("client_id")}
                redirectUri={getUrlParameter("redirect_uri")}
                state={getUrlParameter("state")}
                tryDirectLogin={getUrlParameter("sso") === "myendress"}
              />
              <br />
              <p>
                <a href="/app/id/api/users/register_with_myendress">
                  <FormattedMessage id="signin.signup_new_account.myendress" />
                </a>
              </p>
            </div>
          </Column>
        )}
        {flipper.loginWithMyEndressHauser && (
          <Column md="1" className="separator">
            <span>
              <FormattedMessage id="signin.signup_new_account.separator" />
            </span>
          </Column>
        )}

        <Column className="signin-form" md="6">
          {showNetilionLoginForm ? (
            <div>
              <SignInForm
                onSubmit={handleOnSubmit}
                captchaRequired={captchaRequired}
                captchaRefresh={captchaRefresh}
                showEndressWarning={!!flipper?.loginWithMyEndressHauser}
              />
              <br />
              <p>
                <Link
                  to={{
                    pathname: url("/sign_up"),
                  }}
                >
                  <FormattedMessage id="signin.signup_new_account.netilion" />
                </Link>
              </p>
              <p>
                <FormattedMessage id="signin.forgot_password" />
                <Link
                  to={url("/request_password_reset")}
                  id="request_password_reset"
                >
                  <FormattedMessage id="signin.request_password_reset" />
                </Link>
              </p>
            </div>
          ) : (
            <Button
              onClick={() => setShowNetilionLoginForm(true)}
              id="netilion-sign-in"
              text={intl.formatMessage({
                id: "signin.button.signin.netilion",
              })}
            />
          )}
        </Column>
      </Row>
    );
  };

  return (
    <Container>
      {otpState ? (
        <>
          <Row>
            <Column>
              <Heading
                title={intl.formatMessage({ id: "signin.otp_header" })}
                id="signin.header"
              />
            </Column>
          </Row>
          <Row>
            <Column md="6">
              <SignInOtpForm
                onSubmit={handleOnSubmit}
                onCancel={() => setOtpState(undefined)}
                initialValues={otpState}
              />
            </Column>
          </Row>
        </>
      ) : null}
      {otpEnforcedState ? (
        <>
          <Row>
            <Column md="8">
              <Heading
                title={intl.formatMessage({ id: "signin.otp_forced_header" })}
                id="signin-header"
              />
              <p className="space-after">
                <FormattedMessage id="signin.otp_forced_description" />
              </p>
            </Column>
          </Row>
          <Row>
            <Column md="12">
              <MultiFactorAuthenticationForm
                onSubmit={handleOnSubmit}
                initialValues={otpEnforcedState.credentials}
                provisioningUri={otpEnforcedState.provisioningUri}
              />
            </Column>
          </Row>
        </>
      ) : null}
      {renderLoginChoice()}
      <RecoveryCodesModal
        show={
          (otpEnforcedFinishedState as $TSFixMe)?.recoveryCodes !== undefined
        }
        recoveryCodes={(otpEnforcedFinishedState as $TSFixMe)?.recoveryCodes}
        onClose={handleOnCloseRecoveryCodesModal}
      />
    </Container>
  );
}

export default withBackend(SignIn);
